import React from 'react';
import { Button, Form, RowActions, Table } from '@/components/ui';
import InfoTooltip from '../../../components/InfoTooltip';
import type {
  HiredEquipmentGridProps,
  HiredEquipmentGridRow,
  PurchaseOrderOption,
  ResourceOption,
  TaskOption,
} from '../types';
import { LUMP_SUM_RESOURCE_LABEL } from '../types';
import {
  INTERNAL_MATERIALS_ARIA,
  INTERNAL_MATERIALS_BADGE,
  INTERNAL_MATERIALS_COLUMN,
  INTERNAL_MATERIALS_NOTES_PLACEHOLDER,
  INTERNAL_MATERIALS_TOOLTIP,
} from '../config/internalMaterialsCopy';
import { useHiredEquipmentFieldVisibility } from '../hooks/useHiredEquipmentFieldVisibility';
import { defaultFuelSurchargeEligible, defaultFuelSurchargeEligibleFromVehicleType } from '../utils/fuelSurchargeDefault';
import { fuelSurchargePerHourForLine, lookupFuelSurchargePerHour } from '../utils/fuelSurchargeLookup';
import { getGridRowLocks } from '../utils/gridLocks';
import { formatCurrency, lineAmount, sumAmounts } from '../utils/gridMath';
import { gridLineElementId, isRequiredFieldMissing, qtyStepForUnit, rowHasMissingRequired, validateGridRows, type RequiredGridField } from '../utils/gridValidation';
import { hasHePaperTicket } from '../utils/heSummaryRows';
import {
  canToggleHeOvertime,
  defaultOvertimeEligible,
  formatHeOvertimeBreakdownText,
  formatHeOvertimeFooterText,
} from '../utils/overtime';
import { formatTaskLabel, normalizeLumpSumRow } from '../utils/poPrefill';
import { formatPoOptionLabel } from '../utils/poDisplay';
import { filterPosForTask, nextRowAfterPoChange, nextRowAfterTaskChange, scopedTasksForPoRow } from '../utils/poTaskFilter';
import HiredEquipmentTaskPicker from './HiredEquipmentTaskPicker';
import RowAttachmentCell from './RowAttachmentCell';

const FUEL_SURCHARGE_COLUMN = 'Fuel';
const FUEL_SURCHARGE_ARIA = 'Fuel surcharge eligible';
const FUEL_SURCHARGE_TOOLTIP =
  'Fuel surcharge $/hr from the fuels table for this line’s date and vehicle type (On-Road / Off-Road). N/A is $0. Uncheck to exclude it from the amount.';
const FUEL_SURCHARGE_UNCONFIGURED =
  'Vehicle type is not On-Road or Off-Road — set eligibility explicitly if this line should receive a fuel surcharge.';
const FUEL_SURCHARGE_OVERRIDE_TOOLTIP =
  'Type a fuel surcharge $/hr for this line only. It does not change Fuel Management or the resource. Clear the field to use the fuels-table rate.';

const LUMP_SUM_ADD_LABEL = 'Add lump sum row';
const LUMP_SUM_ADD_TOOLTIP =
  'A flat dollar amount on this task — no resource, quantity, rate, fuel surcharge, or overtime. Type the amount in the Amount column.';

const OVERTIME_ELIGIBLE_COLUMN = 'Overtime eligible';
const OVERTIME_ELIGIBLE_ARIA = 'Overtime eligible';
const OVERTIME_ELIGIBLE_TOOLTIP =
  'Shown when the unit is Hours and the resource is overtime eligible. Hours after 8 (up to 12) bill at 1.5× the rate; hours after 12 bill at 2×. First 8 hours stay at 1×.';

const MoneyHover: React.FC<{ text: string | null; id: string; children: React.ReactNode }> = ({
  text,
  id,
  children,
}) => {
  if (!text) return <>{children}</>;
  return (
    <InfoTooltip content={<span className="tw-whitespace-pre-line">{text}</span>} id={id}>
      {children}
    </InfoTooltip>
  );
};

const CELL =
  'tw-w-full tw-min-w-0 tw-rounded tw-border tw-px-1.5 tw-py-1 tw-text-sm tw-text-content ' +
  'focus:tw-border-line-focus focus:tw-outline-none focus:tw-shadow-focus disabled:tw-bg-surface-alt';

function cellClass(invalid: boolean): string {
  return `${CELL} ${
    invalid
      ? 'tw-border-2 tw-border-danger tw-bg-danger/20 tw-font-medium'
      : 'tw-border-line-input tw-bg-surface'
  }`;
}

function taskDisplay(task: TaskOption): string {
  return formatTaskLabel(task);
}

function posForVendor(purchaseOrders: PurchaseOrderOption[], vendorId: number | null | undefined): PurchaseOrderOption[] {
  if (vendorId == null) return [];
  return purchaseOrders.filter((po) => po.vendorId === vendorId);
}

function resourceOptionEl(resource: ResourceOption) {
  return (
    <option key={resource.id} value={resource.id}>
      {resource.label}
    </option>
  );
}

function ResourceSelectOptions({ options }: { options: ResourceOption[] }) {
  const onPo = options.filter((resource) => resource.onPurchaseOrder === true);
  const other = options.filter((resource) => resource.onPurchaseOrder === false);
  if (onPo.length > 0 && other.length > 0) {
    return (
      <>
        <optgroup label="On this PO">{onPo.map(resourceOptionEl)}</optgroup>
        <optgroup label="Other units">{other.map(resourceOptionEl)}</optgroup>
      </>
    );
  }
  return <>{options.map(resourceOptionEl)}</>;
}

interface CellProps {
  invalid: boolean;
  children: React.ReactNode;
  className?: string;
}

const Td: React.FC<CellProps> = ({ invalid, children, className = '' }) => (
  <td className={`${invalid ? 'tw-bg-danger/20' : ''} ${className}`}>{children}</td>
);

// `tw-block tw-truncate`: locked (read-only) values used to be plain inline spans that would
// wrap onto multiple lines when their column got squeezed (e.g. long vendor/task names at
// higher browser zoom). Because table auto-layout sizes a column from the *narrowest* it can
// get away with, a wrappable cell effectively volunteers its column's width away to neighbors —
// which is exactly what starved the Fuel column down to unreadable. Truncating instead keeps
// every column at the width its header declares (see the `tw-min-w-*` classes below) and pushes
// the overflow into the table's own horizontal scrollbar. `title` restores the full value on
// hover so nothing is actually lost.
const LockedText: React.FC<{ children: React.ReactNode; title?: string }> = ({ children, title }) => (
  <span className="tw-block tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-text-content" title={title}>
    {children || '—'}
  </span>
);

const IntMatCheckFlag: React.FC<{ flagged: boolean }> = ({ flagged }) =>
  flagged ? (
    <span
      aria-hidden="true"
      className="tw-pointer-events-none tw-absolute tw-right-0 tw-top-0 tw-h-0 tw-w-0 tw-border-b-[7px] tw-border-l-0 tw-border-r-[7px] tw-border-t-0 tw-border-b-transparent tw-border-r-primary"
    />
  ) : null;

function parseNumber(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

const HiredEquipmentGrid: React.FC<HiredEquipmentGridProps> = ({
  anchor,
  rows,
  onRowsChange,
  vendors,
  taskOptionForRow,
  searchTasks,
  purchaseOrders,
  tasksByPoId = {},
  resourcesForRow,
  fuelSurcharges = [],
  attemptedSubmit = false,
  onAddLine,
  onAddLumpSumLine,
  onDuplicateLine,
  onRemoveLine,
  onReAddPoResources,
  readOnly = false,
  onCreateTask,
  onViewTask,
  cloningClientIds,
}) => {
  const { showFinancials, canOverrideFuel } = useHiredEquipmentFieldVisibility();
  const locks = getGridRowLocks(anchor);
  const showReAdd = anchor.entryMethod === 'purchase_order' && Boolean(onReAddPoResources);
  const rowsWithFuel = rows.map((row) => ({
    ...row,
    fuelPerHour: fuelSurchargePerHourForLine(fuelSurcharges, row),
  }));
  const totalAmount = sumAmounts(rowsWithFuel);
  const footerOtText = formatHeOvertimeFooterText(rowsWithFuel);
  const incompleteCount = attemptedSubmit ? validateGridRows(rows).invalidCount : 0;
  const showTicketColumn = !readOnly || rows.some((row) => hasHePaperTicket(row.ticketNumber));
  // # Date PO Task Vendor Resource Qty Unit Rate [Fuel] | Amount | Int.Mat OT Ticket Attachment Actions
  const footerLeadColSpan = 9 + (showFinancials ? 1 : 0);
  const footerTrailColSpan = 3 + (showTicketColumn ? 1 : 0) + (readOnly ? 0 : 1);
  const columnCount = footerLeadColSpan + 1 + footerTrailColSpan;

  const patch = (clientId: string, updates: Partial<HiredEquipmentGridRow>) => {
    // Every call here is a real edit, so any row being patched has now been looked at -
    // clear the "fresh duplicate" shading unconditionally rather than asking each of the
    // dozen call sites below to remember to do it.
    onRowsChange(
      rows.map((row) =>
        row.clientId === clientId
          ? // normalizeLumpSumRow last: a vendor/PO/task change nulls resourceLabel and
            // friends on every row, which would otherwise quietly un-lump this one.
            normalizeLumpSumRow({ ...row, ...updates, duplicatedPending: false })
          : row,
      ),
    );
  };

  const handleVendorMaterialChange = (row: HiredEquipmentGridRow, checked: boolean) => {
    patch(row.clientId, {
      vendorMaterial: checked,
    });
  };

  const invalid = (row: HiredEquipmentGridRow, field: RequiredGridField) =>
    attemptedSubmit && isRequiredFieldMissing(row, field);

  const handleVendorChange = (row: HiredEquipmentGridRow, vendorId: number | '') => {
    if (vendorId === '') {
      patch(row.clientId, {
        vendorId: null,
        vendorName: null,
        poId: null,
        poNumber: null,
        resourceId: null,
        resourceLabel: null,
        vehicleType: null,
        fuelSurchargeEligible: null,
        fuelSurchargeOverridePerHour: null,
        overtimeEligible: false,
        resourceOvertimeEligible: false,
      });
      return;
    }
    const vendor = vendors.find((item) => item.id === vendorId);
    patch(row.clientId, {
      vendorId,
      vendorName: vendor?.name ?? null,
      poId: null,
      poNumber: null,
      resourceId: null,
      resourceLabel: null,
      vehicleType: null,
      fuelSurchargeEligible: defaultFuelSurchargeEligible(vendor),
      fuelSurchargeOverridePerHour: null,
      overtimeEligible: false,
      resourceOvertimeEligible: false,
    });
  };

  const handlePoChange = (row: HiredEquipmentGridRow, poId: number | '') => {
    const po = poId === '' ? null : purchaseOrders.find((item) => item.id === poId) ?? null;
    patch(row.clientId, nextRowAfterPoChange(row, po, tasksByPoId, !locks.rowTaskSelectable));
  };

  const lockedTaskOption = (row: HiredEquipmentGridRow): TaskOption | null =>
    taskOptionForRow(row) ?? (row.taskId != null
      ? { id: row.taskId, description: row.taskLabel || `Task ${row.taskId}` }
      : null);

  const handleTaskChange = (row: HiredEquipmentGridRow, task: TaskOption | null) => {
    // When the submission is anchored to a PO the row renders it as locked text with no picker, so
    // releasing it here would strand the row with a blank PO and no way to refill it.
    patch(row.clientId, nextRowAfterTaskChange(row, task, tasksByPoId, taskDisplay, locks.poLocked));
  };

  const handleResourceChange = (row: HiredEquipmentGridRow, resourceId: number | '', options: ResourceOption[]) => {
    if (resourceId === '') {
      patch(row.clientId, {
        resourceId: null,
        resourceLabel: null,
        vehicleType: null,
        fuelSurchargeEligible: null,
        fuelSurchargeOverridePerHour: null,
        overtimeEligible: false,
        resourceOvertimeEligible: false,
      });
      return;
    }
    const resource = options.find((item) => item.id === resourceId);
    const unit = resource?.unit ?? row.unit;
    const resourceOt = resource?.overtimeEligible === true;
    patch(row.clientId, {
      resourceId,
      resourceLabel: resource?.label ?? null,
      unit,
      rate: resource?.rate ?? row.rate,
      vehicleType: resource?.vehicleType ?? null,
      fuelSurchargeEligible: defaultFuelSurchargeEligibleFromVehicleType(resource?.vehicleType),
      fuelSurchargeOverridePerHour: null,
      overtimeEligible: defaultOvertimeEligible(resourceOt, unit),
      resourceOvertimeEligible: resourceOt,
      ...(resource?.taskId && !row.taskId
        ? { taskId: resource.taskId, taskLabel: resource.taskLabel ?? row.taskLabel }
        : {}),
    });
  };

  return (
    // `tw-flex-shrink-0` matters here: this card sits as a flex item inside the page's
    // bounded-height flex column (HiredEquipmentGridPage / HiredEquipmentDetailedView).
    // Because the card also has `overflow-hidden` (for the rounded corners), its flexbox
    // "automatic minimum size" would otherwise be 0 — so once enough lines were added to
    // exceed the viewport, the flex layout squashed/clipped this card down to fit instead
    // of growing and letting the page's own overflow-y-auto scroll. flex-shrink-0 keeps
    // it at its full natural height so the page scrolls to reveal new lines and Add line.
    <div className="tw-flex-shrink-0 tw-overflow-hidden tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-shadow-card">
      <fieldset disabled={readOnly} className="tw-m-0 tw-min-w-0 tw-border-0 tw-p-0">
      {rows.length === 0 ? (
        <p className="tw-mb-0 tw-px-4 tw-py-10 tw-text-center tw-text-sm tw-text-content-muted">
          {readOnly ? 'No line items on this submission.' : 'No lines yet. Use Add line below.'}
        </p>
      ) : (
        <div className="tw-w-full tw-overflow-x-auto">
          <Table hover size="sm" className="tw-mb-0">
            <thead>
              <tr>
                <th className="tw-w-8">#</th>
                <th className="tw-min-w-[7rem]">Date*</th>
                <th className="tw-min-w-[7rem]">PO</th>
                <th className="tw-min-w-[14rem]">Task*</th>
                <th className="tw-min-w-[9rem]">Vendor*</th>
                <th className="tw-min-w-[9rem]">Resource*</th>
                <th className="tw-min-w-[5rem]">Qty*</th>
                <th className="tw-min-w-[4rem]">Unit</th>
                <th className="tw-min-w-[6rem]">Rate*</th>
                {showFinancials && (
                  <th className="!tw-overflow-visible tw-min-w-[11rem]">
                    <InfoTooltip content={FUEL_SURCHARGE_TOOLTIP} id="he-fuel-col">
                      <span className="tw-inline-flex tw-items-center tw-gap-1">
                        {FUEL_SURCHARGE_COLUMN}
                        <i className="bi bi-info-circle tw-text-content-muted" aria-hidden="true" />
                      </span>
                    </InfoTooltip>
                  </th>
                )}
                <th className="tw-min-w-[6rem]">Amount</th>
                <th className="!tw-overflow-visible tw-min-w-[6rem]">
                  <InfoTooltip content={INTERNAL_MATERIALS_TOOLTIP} id="he-int-mat-col">
                    <span className="tw-inline-flex tw-items-center tw-gap-1">
                      {INTERNAL_MATERIALS_COLUMN}
                      <i className="bi bi-info-circle tw-text-content-muted" aria-hidden="true" />
                    </span>
                  </InfoTooltip>
                </th>
                <th className="!tw-overflow-visible tw-min-w-[8rem]">
                  <InfoTooltip content={OVERTIME_ELIGIBLE_TOOLTIP} id="he-ot-col">
                    <span className="tw-inline-flex tw-items-center tw-gap-1">
                      {OVERTIME_ELIGIBLE_COLUMN}
                      <i className="bi bi-info-circle tw-text-content-muted" aria-hidden="true" />
                    </span>
                  </InfoTooltip>
                </th>
                {showTicketColumn && <th className="tw-min-w-[7rem]">Ticket</th>}
                <th className="tw-min-w-[8rem]">Attachment</th>
                {!readOnly && <th className="tw-min-w-[4rem] tw-text-right"> </th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const fuelPerHour = fuelSurchargePerHourForLine(fuelSurcharges, row);
                const tableFuelPerHour = lookupFuelSurchargePerHour(fuelSurcharges, row.vehicleType, row.date);
                // Already $/hr, so the override field can be pre-filled with the table rate directly —
                // no percent conversion needed.
                const fuelOverrideDisplay = row.fuelSurchargeOverridePerHour ?? tableFuelPerHour;
                const amount = lineAmount(row.qty, row.rate, row.overtimeEligible, row.unit, fuelPerHour);
                const otBreakdown = formatHeOvertimeBreakdownText({
                  qty: row.qty,
                  rate: row.rate,
                  overtimeEligible: row.overtimeEligible,
                  unit: row.unit,
                  fuelPerHour,
                });
                const otToggle = canToggleHeOvertime(row.resourceOvertimeEligible, row.unit);
                const resourceOptions = resourcesForRow(row);
                const vendorPos = filterPosForTask(
                  posForVendor(purchaseOrders, row.vendorId),
                  locks.rowTaskSelectable ? row.taskId : (anchor.lockedTaskId ?? row.taskId),
                  tasksByPoId,
                );
                if (row.poId != null && !vendorPos.some((po) => po.id === row.poId)) {
                  const current = purchaseOrders.find((po) => po.id === row.poId);
                  if (current) vendorPos.push(current);
                }
                const lineNumber = index + 1;
                const rowIncomplete = attemptedSubmit && rowHasMissingRequired(row);
                const cloning = cloningClientIds?.has(row.clientId) ?? false;
                return (
                  <React.Fragment key={row.clientId}>
                  <tr
                    id={gridLineElementId(lineNumber)}
                    data-incomplete={rowIncomplete ? 'true' : undefined}
                    data-duplicated-pending={row.duplicatedPending ? 'true' : undefined}
                    title={row.duplicatedPending ? 'Duplicated line — edit any field to confirm it' : undefined}
                    // Table's own `hover` prop targets `tbody tr:hover` with higher specificity
                    // than a per-row hover: utility, so it wins on hover regardless - a plain
                    // background here is all that reliably shows. /20 rather than /5: most of
                    // a row's area is occluded by each cell's own white input box, so a subtle
                    // tint only shows in the cell padding and reads as barely-there.
                    className={row.duplicatedPending ? 'tw-bg-content/20' : undefined}
                  >
                    <td className="tw-text-content-muted">{lineNumber}</td>
                    <Td invalid={invalid(row, 'date')}>
                      <Form.Control
                        type="date"
                        aria-label={`Date, line ${index + 1}`}
                        value={row.date}
                        className={cellClass(invalid(row, 'date'))}
                        onChange={(e) => patch(row.clientId, { date: e.target.value })}
                      />
                    </Td>
                    <td>
                      {locks.rowPoSelectable ? (
                        <Form.Select
                          aria-label={`Purchase order, line ${index + 1}`}
                          value={row.poId ?? ''}
                          disabled={!row.vendorId && !anchor.lockedVendorId}
                          className={cellClass(false)}
                          onChange={(e) => handlePoChange(row, e.target.value ? Number(e.target.value) : '')}
                        >
                          <option value="">{row.vendorId ? 'Select PO...' : 'Pick a vendor first'}</option>
                          {vendorPos.map((po) => (
                            <option key={po.id} value={po.id}>
                              {formatPoOptionLabel(po)}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <LockedText title={row.poNumber ?? undefined}>{row.poNumber}</LockedText>
                      )}
                    </td>
                    <Td invalid={invalid(row, 'task')}>
                      {locks.rowTaskSelectable ? (
                        <HiredEquipmentTaskPicker
                          selected={taskOptionForRow(row)}
                          onChange={(task) => handleTaskChange(row, task)}
                          searchTasks={searchTasks}
                          scopedOptions={scopedTasksForPoRow(
                            row,
                            tasksByPoId,
                            resourceOptions,
                            taskOptionForRow(row),
                          )}
                          invalid={invalid(row, 'task')}
                          onCreateTask={onCreateTask ? () => onCreateTask(row) : undefined}
                          onViewTask={onViewTask ? (task) => onViewTask(task, row) : undefined}
                        />
                      ) : (
                        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1">
                          <LockedText title={row.taskLabel ?? undefined}>{row.taskLabel}</LockedText>
                          {onViewTask && lockedTaskOption(row) && (
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="tw-shrink-0 tw-px-1.5"
                              title="View task details"
                              aria-label={`View task ${row.taskLabel ?? row.taskId}`}
                              onClick={() => onViewTask(lockedTaskOption(row)!, row)}
                            >
                              <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                            </Button>
                          )}
                        </span>
                      )}
                    </Td>
                    <Td invalid={invalid(row, 'vendor')}>
                      {locks.rowVendorSelectable ? (
                        <Form.Select
                          aria-label={`Vendor, line ${index + 1}`}
                          value={row.vendorId ?? ''}
                          className={cellClass(invalid(row, 'vendor'))}
                          onChange={(e) => handleVendorChange(row, e.target.value ? Number(e.target.value) : '')}
                        >
                          <option value="">Select vendor...</option>
                          {vendors.map((vendor) => (
                            <option key={vendor.id} value={vendor.id}>
                              {vendor.name}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <LockedText title={row.vendorName ?? undefined}>{row.vendorName}</LockedText>
                      )}
                    </Td>
                    <Td invalid={invalid(row, 'resource')}>
                      {row.lumpSum ? (
                        <LockedText>
                          <span aria-label={`Resource, line ${index + 1}`}>
                            {LUMP_SUM_RESOURCE_LABEL}
                          </span>
                        </LockedText>
                      ) : (
                        <Form.Select
                          aria-label={`Resource, line ${index + 1}`}
                          value={row.resourceId ?? ''}
                          className={cellClass(invalid(row, 'resource'))}
                          onChange={(e) =>
                            handleResourceChange(row, e.target.value ? Number(e.target.value) : '', resourceOptions)
                          }
                        >
                          <option value="">Select resource...</option>
                          <ResourceSelectOptions options={resourceOptions} />
                        </Form.Select>
                      )}
                    </Td>
                    <Td invalid={invalid(row, 'qty')} className="tw-min-w-[7rem]">
                      {row.lumpSum ? (
                        <LockedText>
                          <span aria-label={`Quantity, line ${index + 1}`}>—</span>
                        </LockedText>
                      ) : (
                        <Form.Control
                          type="number"
                          inputMode={qtyStepForUnit(row.unit) === '1' ? 'numeric' : 'decimal'}
                          step={qtyStepForUnit(row.unit)}
                          aria-label={`Quantity, line ${index + 1}`}
                          value={row.qty ?? ''}
                          // Explicit width, same reasoning as the Amount box: the native
                          // number-input spinner arrows plus cell padding otherwise clip a
                          // multi-digit quantity down to ~2 visible digits.
                          className={`${cellClass(invalid(row, 'qty'))} tw-w-24`}
                          onChange={(e) => patch(row.clientId, { qty: parseNumber(e.target.value) })}
                        />
                      )}
                    </Td>
                    <td className="tw-min-w-[4rem]">
                      <LockedText>
                        <span aria-label={`Unit, line ${index + 1}`}>{row.unit || '—'}</span>
                      </LockedText>
                    </td>
                    <Td invalid={!row.lumpSum && invalid(row, 'rate')} className="tw-min-w-[6rem]">
                      <LockedText>
                        <span aria-label={`Rate, line ${index + 1}`}>
                          {row.lumpSum || row.rate == null ? '—' : formatCurrency(row.rate)}
                        </span>
                      </LockedText>
                    </Td>
                    {showFinancials && (
                      <td className="tw-whitespace-nowrap tw-text-center">
                        {row.lumpSum ? (
                          <span
                            aria-label={`Fuel surcharge, line ${index + 1}`}
                            className="tw-text-sm tw-text-content-muted"
                          >
                            —
                          </span>
                        ) : (
                        <div className="tw-inline-flex tw-items-center tw-gap-1.5">
                          <InfoTooltip
                            content={
                              canOverrideFuel && row.fuelSurchargeEligible === true
                                ? FUEL_SURCHARGE_OVERRIDE_TOOLTIP
                                : row.fuelSurchargeEligible == null
                                  ? FUEL_SURCHARGE_UNCONFIGURED
                                  : FUEL_SURCHARGE_TOOLTIP
                            }
                            id={`he-fuel-line-${index + 1}`}
                          >
                            <Form.Check
                              aria-label={`${FUEL_SURCHARGE_ARIA}, line ${index + 1}`}
                              checked={row.fuelSurchargeEligible === true}
                              onChange={(e) =>
                                patch(row.clientId, { fuelSurchargeEligible: e.currentTarget.checked })
                              }
                            />
                          </InfoTooltip>
                          {canOverrideFuel && row.fuelSurchargeEligible === true && !readOnly ? (
                            <>
                              <span className="tw-text-sm tw-text-content-muted" aria-hidden="true">
                                $
                              </span>
                              <Form.Control
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                aria-label={`Fuel surcharge override, line ${index + 1}`}
                                value={fuelOverrideDisplay ?? ''}
                                // Was `tw-w-16` (4rem/64px) -- with the native number-input spinner
                                // arrows plus cell padding eating into that, there was only room for
                                // ~2 digits, so e.g. "48.65" rendered as a clipped "48". Widened so a
                                // full value (including a 3-digit dollar amount) stays visible.
                                className={`${cellClass(false)} tw-w-24 tw-text-right tw-tabular-nums`}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  patch(row.clientId, {
                                    fuelSurchargeOverridePerHour: raw.trim() === '' ? null : parseNumber(raw),
                                  });
                                }}
                              />
                              {/* aria-label kept off this suffix on purpose: the input above is
                                  already the $/hr value. A second, currency-formatted echo of
                                  the same number here is the redundant "two numbers for one
                                  thing" display this whole cell used to show as `29.36 %
                                  $45.15` — one figure, one control. */}
                              <span className="tw-text-sm tw-text-content-muted" aria-hidden="true">
                                /hr
                              </span>
                            </>
                          ) : (
                            <span
                              aria-label={`Fuel surcharge, line ${index + 1}`}
                              className="tw-text-sm tw-tabular-nums"
                            >
                              {row.fuelSurchargeEligible === true ? formatCurrency(fuelPerHour) : '—'}
                            </span>
                          )}
                        </div>
                        )}
                      </td>
                    )}
                    {row.lumpSum ? (
                      <Td invalid={invalid(row, 'rate')} className="tw-min-w-[9rem]">
                        <Form.Control
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          aria-label={`Amount, line ${index + 1}`}
                          value={row.rate ?? ''}
                          // Explicit width, not just `tw-w-full` off the cell: the native
                          // number-input spinner arrows plus cell padding otherwise ate into a
                          // narrow column, clipping a lump sum amount down to ~2 visible digits.
                          // Widened so a 5+ digit dollar amount (e.g. "12345.67") stays visible.
                          className={`${cellClass(invalid(row, 'rate'))} tw-w-32 tw-text-right tw-tabular-nums`}
                          onChange={(e) => patch(row.clientId, { rate: parseNumber(e.target.value) })}
                        />
                      </Td>
                    ) : (
                    <td className="tw-whitespace-nowrap tw-text-sm tw-tabular-nums">
                      <MoneyHover text={otBreakdown} id={`he-amount-ot-${index + 1}`}>
                        {formatCurrency(amount)}
                      </MoneyHover>
                    </td>
                    )}
                    <td className="tw-text-center">
                      <span className="tw-relative tw-inline-flex">
                        <Form.Check
                          aria-label={`${INTERNAL_MATERIALS_ARIA}, line ${index + 1}`}
                          checked={row.vendorMaterial}
                          onChange={(e) => handleVendorMaterialChange(row, e.currentTarget.checked)}
                        />
                        <IntMatCheckFlag flagged={row.vendorMaterial} />
                      </span>
                    </td>
                    <td className="tw-text-center">
                      {otToggle ? (
                        <Form.Check
                          aria-label={`${OVERTIME_ELIGIBLE_ARIA}, line ${index + 1}`}
                          checked={row.overtimeEligible}
                          onChange={(e) =>
                            patch(row.clientId, { overtimeEligible: e.currentTarget.checked })
                          }
                        />
                      ) : (
                        <span className="tw-text-content-muted" aria-hidden="true">
                          —
                        </span>
                      )}
                    </td>
                    {showTicketColumn && (
                    <td className="tw-min-w-[7rem] tw-w-28">
                      {readOnly ? (
                        <span className="tw-text-sm tw-text-content">{row.ticketNumber?.trim() ?? ''}</span>
                      ) : (
                        <Form.Control
                          type="text"
                          aria-label={`Ticket number, line ${index + 1}`}
                          placeholder="Optional"
                          value={row.ticketNumber ?? ''}
                          className={cellClass(false)}
                          onChange={(e) => patch(row.clientId, { ticketNumber: e.target.value })}
                        />
                      )}
                    </td>
                    )}
                    <td>
                      <RowAttachmentCell
                        attachment={row.attachment}
                        lineNumber={lineNumber}
                        onChange={(attachment) => patch(row.clientId, { attachment })}
                      />
                    </td>
                    {!readOnly && (
                    <td className="tw-text-right">
                      <RowActions
                        maxInline={2}
                        actions={[
                          {
                            kind: 'custom',
                            icon: cloning ? 'bi-hourglass-split' : 'bi-copy',
                            label: 'Duplicate',
                            variant: 'outline-secondary',
                            disabled: cloning,
                            onClick: () => onDuplicateLine(row.clientId),
                          },
                          {
                            kind: 'delete',
                            onClick: () => onRemoveLine(row.clientId),
                          },
                        ]}
                      />
                    </td>
                    )}
                  </tr>
                  {row.vendorMaterial && (
                    <tr className="tw-bg-primary/10 [&:hover]:tw-bg-primary/10">
                      <td colSpan={columnCount} className="tw-px-3 tw-py-2">
                        <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-3">
                          <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-rounded-full tw-bg-primary tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white">
                            {INTERNAL_MATERIALS_BADGE}
                          </span>
                          <Form.Control
                            aria-label={`${INTERNAL_MATERIALS_ARIA} notes, line ${lineNumber}`}
                            placeholder={INTERNAL_MATERIALS_NOTES_PLACEHOLDER}
                            value={row.vendorMaterialNotes ?? ''}
                            className={`${cellClass(false)} tw-min-w-[12rem] tw-flex-1`}
                            onChange={(e) =>
                              patch(row.clientId, { vendorMaterialNotes: e.target.value })
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="tw-bg-content tw-text-white [&_td]:tw-bg-content">
                <td colSpan={footerLeadColSpan} className="tw-px-3 tw-py-2 tw-text-sm tw-text-white">
                  {rows.length} line{rows.length === 1 ? '' : 's'}{incompleteCount > 0 ? ` · ${incompleteCount} incomplete` : ''} · Totals
                </td>
                <td className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-sm tw-text-white tw-tabular-nums">
                  <MoneyHover text={footerOtText} id="he-footer-amount-ot">
                    {formatCurrency(totalAmount)}
                  </MoneyHover>
                </td>
                <td colSpan={footerTrailColSpan} aria-hidden="true" />
              </tr>
            </tfoot>
          </Table>
        </div>
      )}
      </fieldset>

      {!readOnly && (
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-line tw-px-4 tw-py-3">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
          <Button variant="outline-primary" size="sm" onClick={onAddLine}>
            <i className="bi bi-plus-lg" />
            Add line
          </Button>
          {onAddLumpSumLine && (
            <InfoTooltip content={LUMP_SUM_ADD_TOOLTIP} id="he-add-lump-sum">
              <Button variant="outline-secondary" size="sm" onClick={onAddLumpSumLine}>
                <i className="bi bi-cash" />
                {LUMP_SUM_ADD_LABEL}
              </Button>
            </InfoTooltip>
          )}
          {showReAdd && (
            <button
              type="button"
              onClick={onReAddPoResources}
              className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-text-primary hover:tw-underline"
            >
              Re-add all PO resources
            </button>
          )}
        </div>
        <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
          Tip: use Duplicate to repeat a line on a new date — no pop-ups, edit inline like a spreadsheet.
        </p>
      </div>
      )}
    </div>
  );
};

export default HiredEquipmentGrid;
